import React, { useState, useRef, useEffect } from "react";
import styles from "./DetailInfoListUpsert.module.css";
import { PostRadioButton, Header, Footer, useConfirm } from "@/components";
import { AxiosForm } from "@/Axios";
import { useNavigate, useParams } from "react-router-dom";
import { INSERT_TAG } from "..";
import { decoded } from "@/utils";
import {
  useLoadReset,
  useDebouncedCallback,
  useDimLoading,
  useDimLoadComplete,
} from "@/hooks";
import Sortable, { Swap } from "sortablejs";

Sortable.mount(new Swap());
export default function DetailInfoListUpsert() {
  const listRef = useRef(null);
  const dimLoading = useDimLoading();
  const dimLoadComplete = useDimLoadComplete();
  const loadReset = useLoadReset();
  const { tag, info } = useParams();
  const navigate = useNavigate();
  const confirm = useConfirm();
  const defaultState = {
    categoryNm: "",
    imageFile: [],
    preview: [],
    description: "",
    isView: true,
    headerTitle: "심화정보 등록",
    hasImage: false,
    buttonNm: "등록",
    isInsert: true,
    id: "",
    detailCategoryId: "",
    encodedTitle: "",
    isNewOrder: false,
  };
  const [state, setState] = useState(defaultState);
  const resetState = () => {
    [...state.preview].forEach((p) => URL.revokeObjectURL(p));
    setState({ ...defaultState });
    loadReset();
  };
  const fileRef = useRef();

  const triggerFileInput = () => {
    if (!state.preview[0]) {
      fileRef.current.click();
    }
  };
  const fileChange = (e) => {
    const files = Array.from(e.target.files);
    if (files[0]) {
      setState((s) => ({
        ...s,
        imageFile: [...files],
        preview: [...files].map((f) => URL.createObjectURL(f)),
        hasImage: true,
      }));
    }
    // 파일 선택 후 초기화
    e.target.value = "";
  };
  const removeImage = () => {
    setState((s) => ({ ...s, imageFile: [], preview: [] }));
  };
  const changeCategoryName = (e) => {
    setState((s) => ({ ...s, categoryNm: e.target.value }));
    if (e.key === "Enter") {
      e.target.blur();
    }
  };
  const changeDescription = (e) => {
    setState((s) => ({ ...s, description: e.target.value }));
    if (e.key === "Enter") {
      e.target.blur();
    }
  };
  const radioOnOff = (onOff) => {
    setState((s) => ({ ...s, isView: onOff }));
  };
  const insert = useDebouncedCallback(() => {
    const formData = new FormData();
    if (state.hasImage) {
      [...state.imageFile].forEach((file) =>
        formData.append("imageFile", file)
      );
    }
    formData.append("hasImage", state.hasImage);
    formData.append("categoryNm", state.categoryNm);
    formData.append("description", state.description);
    formData.append("isView", state.isView);
    formData.append("detailCategoryId", state.detailCategoryId);

    dimLoading();
    AxiosForm.post("/api/detail-list/insert", formData).then((res) => {
      if (res.data?.errMsg) {
        dimLoadComplete(() => {
          confirm(`${res.data.errMsg}\n(${res.data.errCode})`);
        });
        return;
      }
      dimLoadComplete(() => {
        confirm(
          `수정이 완료되었습니다\n확인을 누르면 목록으로 이동합니다.`,
          () => {
            navigate(
              `/detail-list/${state.detailCategoryId}/${state.encodedTitle}`,
              {
                replace: true,
              }
            );
          }
        );
      });
    });
  });

  const update = useDebouncedCallback(() => {
    const formData = new FormData();
    if (state.hasImage) {
      [...state.imageFile].forEach((file) =>
        formData.append("imageFile", file)
      );
    }

    formData.append("hasImage", state.hasImage);
    formData.append("isNewOrder", state.isNewOrder);
    formData.append("preview", JSON.stringify(state.preview));
    formData.append("categoryNm", state.categoryNm);
    formData.append("description", state.description);
    formData.append("isView", state.isView);
    formData.append("index", state.id);

    dimLoading();
    AxiosForm.post("/api/detail-list/update", formData).then((res) => {
      if (res.data?.errMsg) {
        dimLoadComplete(() => {
          confirm(`${res.data.errMsg}\n(${res.data.errCode})`);
        });
        return;
      }
      dimLoadComplete(() => {
        confirm(
          `수정이 완료되었습니다\n확인을 누르면 목록으로 이동합니다.`,
          () => {
            navigate(
              `/detail-list/${state.detailCategoryId}/${state.encodedTitle}`,
              {
                replace: true,
              }
            );
          }
        );
      });
    });
  });

  const swapBySortable = (evt, list) => {
    const newOrder = [...list];
    const moved = newOrder[evt.oldIndex];
    const swaped = newOrder[evt.newIndex];
    newOrder.splice(evt.newIndex, 1, moved);
    newOrder.splice(evt.oldIndex, 1, swaped);
    return newOrder;
  };

  useEffect(() => {
    const data = decoded(info, true);
    if (tag === INSERT_TAG) {
      setState((s) => ({
        ...s,
        detailCategoryId: data.detailCategoryId,
        encodedTitle: data.title,
      }));
      return resetState;
    }

    setState((s) => ({
      ...s,
      categoryNm: data.categoryNm,
      imageFile: [],
      description: data.description,
      isView: true,
      preview: data?.images?.length > 0 ? [...data.images] : [],
      headerTitle: "심화정보 수정",
      buttonNm: "수정",
      isInsert: false,
      id: data.id,
      detailCategoryId: data.detailCategoryId,
      encodedTitle: data.title,
    }));
    return resetState;
  }, []);
  useEffect(() => {
    if (!listRef.current) return;
    const sortable = Sortable.create(listRef.current, {
      swap: true,
      invertSwap: false,
      swapThreshold: 0.3,
      direction: "vertical",
      animation: 200,
      swapClass: styles.swapHighlight,
      onEnd: (evt) => {
        setState((s) => ({
          ...s,
          imageFile:
            s.imageFile.length > 0
              ? swapBySortable(evt, s.imageFile)
              : s.imageFile,
          preview: swapBySortable(evt, s.preview),
          isNewOrder: tag !== INSERT_TAG && s.imageFile.length === 0,
        }));
      },
    });
    return () => sortable.destroy();
  }, [state.imageFile, state.preview]);
  return (
    <div className={styles.mainContainer}>
      <div className={styles.container}>
        <Header title={state.headerTitle} isShare={false} />
        <div className={styles.infoInputArea}>
          <div className={styles.categoryName}>
            <div className={styles.starIcon} />
            <div className={styles.categoryInput}>
              <input
                className={styles.categoryInputInput}
                placeholder="심화정보 제목을 입력해주세요"
                defaultValue={state.categoryNm}
                onKeyUp={(e) => {
                  changeCategoryName(e);
                }}
              />
            </div>
          </div>
          <div className={styles.mainImage}>
            <div className={styles.mainImageContainer}>
              <div className={styles.starIcon1} />
              <div
                className={styles.mainImageInputArea}
                onClick={triggerFileInput}
              >
                <div className={styles.inputPlaceholder}>
                  {state.preview.length === 0 && (
                    <span className={styles.mainImageMessage}>
                      PNG 파일들을 업로드해주세요
                    </span>
                  )}
                  {state.preview.length > 0 && (
                    <ul ref={listRef}>
                      {state.preview.map((item) => (
                        <li key={item} className={styles.swapLi}>
                          <img
                            className={styles.preview}
                            src={item}
                            alt="미리보기"
                          />
                        </li>
                      ))}
                    </ul>
                  )}
                </div>
                <input
                  ref={fileRef}
                  type="file"
                  accept=".png,image/png"
                  className={styles.mainInput}
                  onChange={(e) => {
                    fileChange(e);
                  }}
                  multiple
                />
                <div className={styles.imageArea}>
                  {state.preview.length === 0 && (
                    <div className={styles.uploadIcon} />
                  )}
                  {state.preview.length > 0 && (
                    <button className={styles.cancelBtn} onClick={removeImage}>
                      <span className={styles.cancel}>X</span>
                    </button>
                  )}
                </div>
              </div>
            </div>
          </div>
          <div className={styles.categoryDescription}>
            <div className={styles.descriptionInput}>
              <div className={styles.inputPlaceholder2}></div>
              <input
                type="text"
                className={styles.descriptionInput3}
                placeholder="심화정보에 대한 설명을 입력해주세요"
                defaultValue={state.description}
                onKeyUp={(e) => {
                  changeDescription(e);
                }}
              />
            </div>
          </div>
          <div className={styles.publishArea}>
            <div className={styles.starIcon4} />
            <PostRadioButton
              postClick={() => {
                radioOnOff(true);
              }}
              notPostClick={() => {
                radioOnOff(false);
              }}
            />
          </div>
        </div>
        <div className={styles.registerButtonArea}>
          <button
            className={styles.registerButton}
            onClick={state.isInsert ? insert : update}
          >
            <span className={styles.register}>{state.buttonNm}</span>
          </button>
        </div>
        <Footer />
      </div>
    </div>
  );
}
